import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle,
  MessageSquare,
  Package,
  Plus,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

import { AgentInput } from "./components/AgentInput";
import { AliasValidationCTA } from "./components/AliasValidationCTA";
import { ChatDrawer } from "./components/ChatDrawer";
import { EmptyState } from "./components/EmptyState";
import { ExpandedPanneauModal } from "./components/ExpandedPanneauModal";
import { LastReplyCard } from "./components/LastReplyCard";
import { NewPanneauModal } from "./components/NewPanneauModal";
import { OrganisationSwitcher } from "./components/OrganisationSwitcher";
import { OrphanProposalsCTA } from "./components/OrphanProposalsCTA";
import { UnidentifiedFlagsCTA } from "./components/UnidentifiedFlagsCTA";
import { PanneauActionsMenu } from "./components/PanneauActionsMenu";
import { PendingPanneauCard } from "./components/PendingPanneauCard";
import { SortablePanneau } from "./components/SortablePanneau";
import { SuggestionPills } from "./components/SuggestionPills";
import { ArchivedPanneauxModal } from "./components/ArchivedPanneauxModal";
import { UndoToast } from "./components/UndoToast";
import { UserMenu } from "./components/UserMenu";
import { useMe } from "./api/auth";
import { ApiError } from "./api/client";
import {
  useArchivedPanneaux,
  useArchivePanneau,
  usePanneaux,
  useRecordVoiceInteraction,
  useRestorePanneau,
  useSendAgentAudio,
  useSendAgentMessage,
} from "./api/queries";
import { useEventStream } from "./api/sse";
import type { Panneau as PanneauModel } from "./api/types";
import { buildVoiceInteractionEvent, type VoiceRecordingMeta } from "./lib/voice_perf";
import {
  applySavedOrder,
  loadOrder,
  saveOrder,
} from "./lib/panneau_order";
import { computeSuggestions } from "./lib/suggestions";
import type { SuggestedAction } from "./api/types";

export default function App() {
  const me = useMe();
  // The conversation_id is now derived from the authenticated session,
  // so it's stable across browsers and devices for the same operator.
  const conv = me.data?.conversation_id ?? "";
  const panneaux = usePanneaux(conv);
  const send = useSendAgentMessage(conv);
  const sendAudio = useSendAgentAudio(conv);
  const recordVoiceInteraction = useRecordVoiceInteraction();
  const [lastReply, setLastReply] = useState<{
    voix: string;
    details: string;
    suggested_actions: SuggestedAction[];
  } | null>(null);
  const [lastProposals, setLastProposals] = useState<import("./api/types").Proposal[]>([]);
  const [agentError, setAgentError] = useState<string | null>(null);
  // Pre-fill the agent input from a tapped suggestion pill. The
  // nonce makes re-tapping the same pill re-apply the prefill.
  const [inputPrefill, setInputPrefill] = useState<{ value: string; nonce: number } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newPanneauOpen, setNewPanneauOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  // Recently-closed-panneau hook for the undo toast. The toast
  // self-dismisses after 5s by calling onDismiss → setRecentlyClosed(null).
  const [recentlyClosed, setRecentlyClosed] = useState<
    { id: string; name: string } | null
  >(null);
  // Single archive mutation reused across the app — the panneau
  // header's close button delegates to App's `onArchivePanneau` so the
  // toast wiring lives in one place.
  const archivePanneau = useArchivePanneau(conv);
  const restorePanneau = useRestorePanneau(conv);
  const archived = useArchivedPanneaux(conv);
  const archivedCount = archived.data?.length ?? 0;
  const isAdmin = me.data?.organisation.role === "administrateur";
  const aliasProposalCount = lastProposals.filter((p) => p.type === "canonical_alias_add").length;
  // Layout: single-panneau-per-row (default) vs. side-by-side at lg+.
  // Persisted in localStorage so user choice sticks across sessions.
  const [splitView, setSplitView] = useState<boolean>(() => {
    try {
      return localStorage.getItem("bunapp.splitView") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("bunapp.splitView", splitView ? "1" : "0");
    } catch {
      /* swallow */
    }
  }, [splitView]);

  // The displayed order. Initialized from the server list reordered by
  // localStorage. Updated optimistically on drag, then persisted.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  useEventStream(conv);

  // Sync orderedIds whenever the server list changes.
  useEffect(() => {
    if (!panneaux.data || !conv) return;
    const saved = loadOrder(conv);
    const ordered = applySavedOrder(panneaux.data, saved);
    setOrderedIds(ordered.map((p) => p.id));
  }, [conv, panneaux.data]);

  const onSend = async (text: string) => {
    try {
      const r = await send.mutateAsync(text);
      setAgentError(null);
      setLastReply({
        voix: r.voix || r.assistant_text,
        details: r.details || r.assistant_text,
        suggested_actions: r.suggested_actions ?? [],
      });
      setLastProposals(r.proposals ?? []);
    } catch (error) {
      setAgentError(resolveAgentErrorMessage(error));
    }
  };

  const onSendAudio = async (
    blob: Blob,
    mimeType?: string,
    meta?: VoiceRecordingMeta,
  ) => {
    const requestStartedAt = nowMs();
    try {
      const r = await sendAudio.mutateAsync({ blob, mimeType });
      const responseReceivedAt = nowMs();
      setAgentError(null);
      setLastReply({
        voix: r.voix || r.assistant_text,
        details: r.details || r.assistant_text,
        suggested_actions: r.suggested_actions ?? [],
      });
      setLastProposals(r.proposals ?? []);
      if (meta) {
        await nextPaint();
        recordVoiceInteraction.mutate(
          buildVoiceInteractionEvent({
            ...meta,
            status: "ok",
            requestStartedAt,
            responseReceivedAt,
            responseRenderedAt: nowMs(),
            blob,
            mimeType,
            transcript: r.transcript,
          }),
        );
      }
    } catch (error) {
      if (meta) {
        recordVoiceInteraction.mutate(
          buildVoiceInteractionEvent({
            ...meta,
            status: "error",
            requestStartedAt,
            responseReceivedAt: nowMs(),
            responseRenderedAt: nowMs(),
            blob,
            mimeType,
          }),
        );
      }
      const recovery = readChatCapacityRecovery(error);
      if (recovery?.transcript) {
        setInputPrefill({
          value: recovery.transcript,
          nonce: Date.now(),
        });
      }
      setAgentError(recovery?.detail ?? resolveAgentErrorMessage(error));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(next);
    saveOrder(conv, next);
  };

  const byId: Record<string, PanneauModel> = {};
  for (const p of panneaux.data ?? []) byId[p.id] = p;
  const orderedPanneaux = orderedIds
    .map((id) => byId[id])
    .filter((p): p is PanneauModel => !!p);

  const expandedSummary = expandedId ? byId[expandedId] : null;

  // First-paint guard: AuthGuard already handled the loading/error
  // states above us, but if we're here without me.data we're in a
  // brief sync gap — return nothing rather than render with no conv.
  if (!me.data) {
    return null;
  }


  function nowMs(): number {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
    return Date.now();
  }


  async function nextPaint(): Promise<void> {
    if (typeof requestAnimationFrame === "function") {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      return;
    }
    await Promise.resolve();
  }

  function pickSuggestedAction(prefill: string): void {
    setInputPrefill({ value: prefill, nonce: Date.now() });
  }

  return (
    <div className="min-h-full flex flex-col">
      {/* Top bar — TWO rows at every breakpoint, ordered so the
            stable nav anchors at the top and the dynamic agent
            surface flows below it:
              row 1 = action bar (always visible, fixed height —
                      anchors the page so it never moves when the
                      reply card / suggestion pills mount or unmount),
              row 2 = agent input + everything that grows out of it
                      (reply card, suggestion pills, panneau-create
                      proposals, orphan CTAs) — this is the
                      "conversation surface".
          Earlier draft swapped these; user-tested feedback was that
          the chip row janked vertically when a reply landed because
          it sat below the input and got pushed by the LastReplyCard
          mounting. With nav on top, only the conversation surface
          grows downward — the action bar stays anchored. */}
      <header className="sticky top-0 z-10 bg-canvas/90 dark:bg-canvas-dark/90 backdrop-blur border-b border-black/5 dark:border-white/10">
        {/* Row 1: three clusters separated by flex-1 spacers so the
            middle nav floats centered:
              left   — panneau actions (+ Panneau, kebab w/ Vue + Archivés)
              middle — cross-cutting nav (Commandes, Conversation)
              right  — identity (Aide, Org, Avatar menu)
            Min-h-9 (36px) is the floor on every tap target.
            Horizontal scroll on overflow keeps the row from
            wrapping on narrow viewports. */}
        <div className="max-w-6xl mx-auto px-3 sm:px-4 pt-2 pb-1.5">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Left: panneau actions — primary CTA + kebab for view
                preferences and archived recovery. Visually clustered
                so they read as one panneau-management surface. */}
            <button
              type="button"
              onClick={() => setNewPanneauOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 min-h-9 px-3 py-2 rounded-full text-sm bg-ink text-white hover:bg-ink/90"
              title="Créer un nouveau panneau"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden md:inline">Panneau</span>
            </button>
            <PanneauActionsMenu
              splitView={splitView}
              setSplitView={(v) => setSplitView(v)}
              archivedCount={archivedCount}
              onOpenArchived={() => setArchivedOpen(true)}
            />

            <div className="flex-1 min-w-2 shrink-0" />

            {/* Middle: cross-cutting nav — these views span all
                panneaux (Commandes shows validated lines from any
                panneau; Conversation shows the agent's history
                across panneaux). Centering them makes the
                "panneau-action / cross-view / identity" rhythm
                explicit. */}
            <Link
              to="/commandes"
              className="shrink-0 inline-flex items-center gap-1.5 min-h-9 px-3 py-2 rounded-full text-sm bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 text-ink dark:text-ink-dark"
              title="Voir les commandes validées"
            >
              <Package className="h-4 w-4" />
              <span className="hidden md:inline">Commandes</span>
            </Link>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 min-h-9 px-3 py-2 rounded-full text-sm bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 text-ink dark:text-ink-dark"
              aria-expanded={historyOpen}
              title="Voir la conversation"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="hidden md:inline">Conversation</span>
            </button>

            <div className="flex-1 min-w-2 shrink-0" />

            {/* Right: identity / app-config — Aide is onboarding
                (icon-only, low-key); Org switcher + Avatar
                dropdown carry profile/theme/logout behind a single
                trigger. */}
            <Link
              to="/aide"
              className="shrink-0 inline-flex items-center justify-center min-h-9 min-w-9 px-2 py-2 rounded-full bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 text-ink dark:text-ink-dark"
              title="Aide — que sait faire l'agent ?"
              aria-label="Aide"
            >
              <HelpCircle className="h-4 w-4" />
            </Link>
            <OrganisationSwitcher />
            <UserMenu />
          </div>
        </div>
        {/* Row 2: agent input — the primary "conversation surface".
            Lives below the action bar so the reply card and
            suggestion pills (which mount immediately under the
            input) grow downward without ever pushing the action
            bar up. */}
        <div className="max-w-6xl mx-auto px-3 sm:px-4 pb-2">
          <AgentInput
            onSend={onSend}
            onSendAudio={onSendAudio}
            pending={send.isPending || sendAudio.isPending}
            prefillValue={inputPrefill?.value}
            prefillNonce={inputPrefill?.nonce}
            agentError={agentError}
          />
        </div>
        {lastReply && (
          <div className="px-4 pb-3">
            <LastReplyCard
              voix={lastReply.voix}
              details={lastReply.details}
              onOpenDrawer={() => setHistoryOpen(true)}
              onDismiss={() => {
                setLastReply(null);
                setLastProposals([]);
              }}
            />
          </div>
        )}
        {isAdmin && aliasProposalCount > 0 && (
          <div className="pb-2">
            <AliasValidationCTA count={aliasProposalCount} />
          </div>
        )}
        {lastReply && (
          <SuggestionPills
            suggestions={computeSuggestions({
              voix: lastReply.voix,
              details: lastReply.details,
              proposals: lastProposals,
              structuredActions: lastReply.suggested_actions,
            })}
            onPick={pickSuggestedAction}
          />
        )}
        {lastProposals.some((p) => p.type === "panneau_create") && (
          <div className="pb-2">
            <PendingPanneauCard
              proposals={lastProposals}
              conv={conv}
              onResolved={(id) =>
                setLastProposals((prev) => prev.filter((p) => p.id !== id))
              }
            />
          </div>
        )}
        {lastProposals.length > 0 && (panneaux.data?.length ?? 0) >= 0 && (
          <div className="pb-2">
            <OrphanProposalsCTA
              proposals={lastProposals}
              panneaux={panneaux.data ?? []}
              onDismiss={() => setLastProposals([])}
            />
          </div>
        )}
        {lastProposals.some((p) => p.type === "unidentified_product_flag") && (
          <div className="pb-2">
            <UnidentifiedFlagsCTA
              proposals={lastProposals}
              onResolved={(id) =>
                setLastProposals((prev) => prev.filter((p) => p.id !== id))
              }
            />
          </div>
        )}
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {panneaux.isLoading && (
            <div className="text-muted dark:text-muted-dark text-sm">Chargement des panneaux…</div>
          )}
          {panneaux.data && panneaux.data.length === 0 && (
            <EmptyState conv={conv} organisationName={me.data.organisation.name} />
          )}
          {orderedPanneaux.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={orderedIds}
                strategy={rectSortingStrategy}
              >
                <div
                  className={`grid grid-cols-1 gap-6 ${
                    splitView ? "lg:grid-cols-2" : ""
                  }`}
                >
                  {orderedPanneaux.map((p) => (
                    <SortablePanneau
                      key={p.id}
                      conv={conv}
                      summary={p}
                      onExpand={(id) => setExpandedId(id)}
                      onArchive={(detail) => {
                        // Single-source-of-truth for close: fire the
                        // mutation here AND set up the undo toast.
                        archivePanneau.mutate(detail.id);
                        setRecentlyClosed({ id: detail.id, name: detail.name });
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </main>

      {expandedSummary && (
        <ExpandedPanneauModal
          conv={conv}
          summary={expandedSummary}
          onClose={() => setExpandedId(null)}
        />
      )}

      <ChatDrawer
        conv={conv}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onPickSuggestedAction={pickSuggestedAction}
      />

      <NewPanneauModal
        open={newPanneauOpen}
        onClose={() => setNewPanneauOpen(false)}
      />

      <ArchivedPanneauxModal
        conv={conv}
        open={archivedOpen}
        onClose={() => setArchivedOpen(false)}
      />

      {/* Undo toast — mounts whenever a panneau is closed and
          self-dismisses after 5s. Annuler restores the panneau
          via the same restore endpoint the modal uses, then
          clears the toast state. */}
      {recentlyClosed && (
        <UndoToast
          message={`Panneau « ${recentlyClosed.name} » fermé.`}
          onUndo={() => restorePanneau.mutate(recentlyClosed.id)}
          onDismiss={() => setRecentlyClosed(null)}
        />
      )}
    </div>
  );
}


function resolveAgentErrorMessage(error: unknown): string {
  const recovery = readChatCapacityRecovery(error);
  if (recovery?.detail) {
    return recovery.detail;
  }
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Une erreur est survenue.";
}


function readChatCapacityRecovery(
  error: unknown,
): { detail: string; transcript?: string } | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== "object") {
    return null;
  }
  const body = error.body as Record<string, unknown>;
  if (body.code !== "chat_capacity_exceeded") {
    return null;
  }
  if (typeof body.detail !== "string") {
    return null;
  }
  return {
    detail: body.detail,
    transcript:
      typeof body.transcript === "string" ? body.transcript : undefined,
  };
}
